// Campaign detail — review the 10 generated emails, edit any, approve, send.
//
// Status flows: draft → approved → sending. The "Approve" button only shows
// in draft. The "Send" button only shows in approved/sending.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  RefreshCw, ChevronLeft, ChevronRight, Eye, X, Save, Send,
  CheckCircle2, AlertTriangle, Clock, Image as ImageIcon, Upload, Mail,
} from "lucide-react";
import EmailMovedBanner from "@/components/email/email-moved-banner";
import ProgressLoader from "@/components/progress-loader";
import { GENERATION_STAGES, SEND_STAGES } from "./admin-campaign-new";

const ADMIN = () => localStorage.getItem("admin_token") || "";

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", "x-admin-token": ADMIN(), ...(init?.headers || {}) },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
  return res.json();
}

interface Campaign {
  id: number;
  name: string;
  track: "investor" | "banker";
  status: "draft" | "approved" | "sending" | "done" | "archived";
  brief: any;
  createdAt: string;
  approvedAt: string | null;
  lastSendStartedAt: string | null;
  generationStatus: "idle" | "generating" | "complete" | "failed";
  generationStartedAt: string | null;
  generationFinishedAt: string | null;
  generationError: string | null;
}

interface Template {
  id: number;
  campaignId: number;
  day: number;
  subject: string;
  introText: string;
  valuePropText: string;
  featureName: string;
  featureBlurb: string;
  screenshotKey: string;
  ctaLabel: string;
  ctaPath: string;
  tomorrowTeaser: string;
  customHtml: string | null;
}

interface Subscription {
  id: number;
  email: string;
  name: string;
  status: string;
  currentDay: number;
  enrolledAt: string;
  isTest: boolean;
}

interface Screenshot { key: string; publicUrl: string }

export default function AdminCampaignDetail({ campaignId }: { campaignId: number }) {
  const [, setLocation] = useLocation();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [screenshots, setScreenshots] = useState<Map<string, Screenshot>>(new Map());
  const [screenshotIssues, setScreenshotIssues] = useState<{ templatesWithoutImage: number; totalTemplates: number; missingKeys: string[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editorDay, setEditorDay] = useState<number | null>(null);
  const [sendOpen, setSendOpen] = useState(false);

  const reload = async () => {
    setLoading(true); setError(null);
    const [cRes, scRes] = await Promise.allSettled([
      api<{ campaign: Campaign; templates: Template[]; subscriptions: Subscription[]; screenshotIssues?: { templatesWithoutImage: number; totalTemplates: number; missingKeys: string[] } }>(`/campaigns/${campaignId}`),
      api<{ screenshots: Screenshot[] }>("/onboarding-drips/screenshots"),
    ]);
    const fails: string[] = [];
    if (cRes.status === "fulfilled") {
      setCampaign(cRes.value.campaign);
      setTemplates(cRes.value.templates || []);
      setSubscriptions(cRes.value.subscriptions || []);
      setScreenshotIssues(cRes.value.screenshotIssues || null);
    } else { fails.push(`campaign: ${cRes.reason?.message || cRes.reason}`); }
    if (scRes.status === "fulfilled") {
      const m = new Map<string, Screenshot>();
      for (const s of scRes.value.screenshots || []) m.set(s.key, s);
      setScreenshots(m);
    } else { fails.push(`screenshots: ${scRes.reason?.message || scRes.reason}`); }
    if (fails.length > 0) setError(`Partial load failure — ${fails.join(" | ")}`);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [campaignId]);

  // Auto-poll while server-side generation is in progress (e.g., user closed
  // the wizard tab mid-generate and reopened the detail page later). Stops
  // as soon as status leaves 'generating'. Without this, the in-progress
  // banner promises auto-refresh that never happens.
  useEffect(() => {
    if (campaign?.generationStatus !== "generating") return;
    if (busy === "regenerate") return; // Wizard polling already runs
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const polled = await api<{ campaign: Campaign; templates: Template[]; subscriptions: Subscription[] }>(`/campaigns/${campaignId}`);
        if (cancelled) return;
        setCampaign(polled.campaign);
        setTemplates(polled.templates || []);
        setSubscriptions(polled.subscriptions || []);
      } catch {
        // transient — keep polling
      }
    };
    const handle = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(handle); };
  }, [campaign?.generationStatus, busy, campaignId]);

  const handleApprove = async () => {
    if (!campaign) return;
    setBusy("approve"); setError(null); setInfo(null);
    try {
      await api(`/campaigns/${campaign.id}/approve`, { method: "POST" });
      setInfo("Campaign approved. Ready to send.");
      await reload();
    } catch (err: any) { setError(err?.message || "Approve failed"); }
    setBusy(null);
  };

  const handleSaveTemplate = async (day: number, fields: Partial<Template>): Promise<Template> => {
    const r = await api<{ template: Template }>(`/campaigns/${campaignId}/templates/${day}`, {
      method: "PATCH", body: JSON.stringify(fields),
    });
    setTemplates((prev) => prev.map((p) => p.day === day ? r.template : p));
    return r.template;
  };

  // Async generation flow (matches the wizard): kick off, then poll
  // GET /campaigns/:id every 2s until generationStatus is complete or failed.
  // Used for both first-time generate (when generation previously failed)
  // and re-generate from scratch (replaces existing 10 templates).
  const handleRegenerate = async () => {
    if (!campaign) return;
    if (campaign.status !== "draft") {
      setError("Can only regenerate drafts. Fork a new campaign instead."); return;
    }
    setBusy("regenerate"); setError(null); setInfo(null);
    try {
      await api(`/campaigns/${campaign.id}/generate`, { method: "POST" });
    } catch (err: any) {
      setError(err?.message || "Failed to start generation");
      setBusy(null);
      return;
    }

    const startedAt = Date.now();
    const TIMEOUT_MS = 5 * 60 * 1000;
    while (Date.now() - startedAt < TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const polled = await api<{ campaign: Campaign }>(`/campaigns/${campaign.id}`);
        const s = polled.campaign.generationStatus;
        if (s === "complete") {
          setInfo("Regenerated.");
          setBusy(null);
          await reload();
          return;
        }
        if (s === "failed") {
          setError(polled.campaign.generationError || "Favcy Navigator is not responding right now. This is usually a transient issue — please click Retry after 10 mins.");
          setBusy(null);
          await reload();
          return;
        }
      } catch {
        // transient, keep polling
      }
    }
    setError("Generation is taking longer than expected. Refresh in a minute to see if it finished.");
    setBusy(null);
    await reload();
  };

  if (loading && !campaign) return <div className="p-8 text-sm text-muted-foreground">Loading...</div>;
  if (!campaign) return <div className="p-8 text-sm text-red-700">Campaign not found.</div>;

  const statusBadge = (() => {
    const map: Record<string, { class: string; icon: any; label: string }> = {
      draft: { class: "bg-amber-500/10 text-amber-700 border-amber-500/30", icon: Clock, label: "Draft" },
      approved: { class: "bg-blue-500/10 text-blue-700 border-blue-500/30", icon: CheckCircle2, label: "Approved" },
      sending: { class: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30", icon: Send, label: "Sending" },
      done: { class: "bg-muted text-muted-foreground border-border", icon: CheckCircle2, label: "Done" },
      archived: { class: "bg-muted text-muted-foreground border-border", icon: CheckCircle2, label: "Archived" },
    };
    return map[campaign.status] || map.draft;
  })();
  const Icon = statusBadge.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link href="/admin/campaigns"><a className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> Back to campaigns</a></Link>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <h1 className="text-2xl font-serif tracking-tight">{campaign.name}</h1>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${statusBadge.class}`}>
              <Icon className="w-3 h-3" /> {statusBadge.label}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-muted text-muted-foreground border border-border">
              {campaign.track === "investor" ? "Investors" : "Advisors"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {campaign.status === "draft" && (
            <button onClick={handleRegenerate} disabled={!!busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md border border-border hover:bg-muted disabled:opacity-50">
              <RefreshCw className="w-3.5 h-3.5" />
              {busy === "regenerate" ? "Regenerating…" : "Regenerate"}
            </button>
          )}
          {campaign.status === "draft" && templates.length === 10 && (
            <button onClick={handleApprove} disabled={!!busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {busy === "approve" ? "Approving…" : "Approve"}
            </button>
          )}
          {(campaign.status === "approved" || campaign.status === "sending") && (
            <button onClick={() => setSendOpen(true)} disabled={!!busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90">
              <Send className="w-3.5 h-3.5" /> Send to a batch
            </button>
          )}
        </div>
      </div>

      {(error || (info && !busy)) && (
        <div className={`p-3 rounded-md text-sm border ${error ? "bg-red-500/10 border-red-500/30 text-red-700" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-700"}`}>
          {error || info}
        </div>
      )}

      {/* Generation-status surface — shown only when something is off-normal.
          Hidden in the happy path (status='complete' AND templates rendered).
          When 'failed' the user gets a one-click Retry that pipes back into
          handleRegenerate (which polls). */}
      {campaign.generationStatus === "generating" && busy !== "regenerate" && (
        <div className="p-3 rounded-md text-sm border bg-amber-500/10 border-amber-500/30 text-amber-800 flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold">Generation in progress on the server</div>
            <div className="text-xs mt-0.5">
              Started {campaign.generationStartedAt ? new Date(campaign.generationStartedAt).toLocaleTimeString() : "recently"}.
              This page will refresh automatically.
            </div>
          </div>
          <button onClick={reload} className="px-3 py-1.5 text-xs rounded-md border border-amber-500/40 hover:bg-amber-500/20">
            Refresh now
          </button>
        </div>
      )}
      {/* Missing-screenshots warning — fires when generation succeeded but
          some templates reference screenshotKeys absent from object storage.
          Most common cause: empty inventory in this environment. The renderer
          silently omits the <img>, so without this banner an admin would
          ship image-less emails without knowing. */}
      {screenshotIssues && screenshotIssues.templatesWithoutImage > 0 && templates.length > 0 && (
        <div className="p-3 rounded-md text-sm border bg-amber-500/10 border-amber-500/30 text-amber-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-1.5">
              <ImageIcon className="w-4 h-4" />
              {screenshotIssues.templatesWithoutImage} of {screenshotIssues.totalTemplates} emails will render without an image
            </div>
            <div className="text-xs mt-0.5 break-words">
              {screenshotIssues.missingKeys.length > 0
                ? <>Missing screenshot {screenshotIssues.missingKeys.length === 1 ? "key" : "keys"}: <span className="font-mono">{screenshotIssues.missingKeys.join(", ")}</span>. </>
                : null}
              Upload the matching feature screenshots and the next render of these emails will include them.
            </div>
          </div>
          <Link href="/admin/onboarding/screenshots">
            <a className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-amber-500/40 hover:bg-amber-500/20">
              <Upload className="w-3.5 h-3.5" /> Upload screenshots
            </a>
          </Link>
        </div>
      )}

      {campaign.generationStatus === "failed" && (
        <div className="p-3 rounded-md text-sm border bg-red-500/10 border-red-500/30 text-red-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Last generation failed</div>
            <div className="text-xs mt-0.5 break-words">
              {campaign.generationError || "Unknown error. Click Retry to try again."}
            </div>
          </div>
          <button onClick={handleRegenerate} disabled={!!busy}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50">
            <RefreshCw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
      )}

      <ProgressLoader
        active={busy === "regenerate"}
        title="Rateek is re-drafting your 10 emails"
        stages={GENERATION_STAGES}
        stageIntervalMs={4500}
        note="This takes 30-90 seconds. Existing emails for this campaign will be replaced."
      />
      <ProgressLoader
        active={busy === "approve"}
        title="Approving the campaign"
        stages={["Locking templates", "Flipping status to approved"]}
        stageIntervalMs={1200}
      />


      {/* Brief sidebar */}
      <details className="rounded-lg border border-border" open={false}>
        <summary className="px-4 py-3 cursor-pointer text-sm font-semibold flex items-center justify-between">
          <span>Brief used for generation</span>
          <span className="text-xs text-muted-foreground font-normal">click to view</span>
        </summary>
        <div className="px-4 pb-4 text-xs space-y-2 text-muted-foreground">
          {Object.entries(campaign.brief || {}).map(([k, v]) => (
            <div key={k} className="grid grid-cols-[140px_1fr] gap-3">
              <span className="font-mono text-[10px] uppercase">{k}</span>
              <span className="whitespace-pre-wrap text-foreground">{String(v)}</span>
            </div>
          ))}
        </div>
      </details>

      {/* Templates */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
          <h2 className="text-sm font-semibold">{templates.length}/10 emails — click any day to edit</h2>
          {templates.length === 0 && <button onClick={handleRegenerate} disabled={!!busy} className="text-xs text-[#FF5C00] hover:underline">Generate now</button>}
        </div>
        {templates.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <AlertTriangle className="w-5 h-5 mx-auto mb-2" />
            No emails generated yet.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {templates.map((t) => {
              const screenshot = screenshots.get(t.screenshotKey);
              return (
                <li key={t.id}>
                  <button onClick={() => setEditorDay(t.day)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md bg-[#FF5C00]/10 text-[#FF5C00] flex items-center justify-center font-mono text-sm font-semibold shrink-0">D{t.day}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{t.subject}</p>
                        {t.customHtml && <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded bg-purple-500/10 text-purple-700 border border-purple-500/30">HTML override</span>}
                        {screenshot ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/10 text-emerald-700">
                            <ImageIcon className="w-3 h-3" /> {t.screenshotKey}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded bg-amber-500/10 text-amber-700">
                            <ImageIcon className="w-3 h-3" /> {t.screenshotKey} (missing)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.introText}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">CTA: {t.ctaLabel} → {t.ctaPath}</p>
                    </div>
                    <div className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-border text-muted-foreground">
                      <Eye className="w-3 h-3" /> Edit
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <EmailMovedBanner />

      {/* Subscriptions for this campaign */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <h2 className="text-sm font-semibold">Recipients ({subscriptions.length})</h2>
        </div>
        {subscriptions.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            <Mail className="w-5 h-5 mx-auto mb-2" />
            No one's been sent this campaign yet.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {subscriptions.slice(0, 50).map((s) => (
              <li key={s.id} className="px-4 py-2 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <p className="font-medium truncate">{s.name} <span className="text-muted-foreground">— {s.email}</span></p>
                  <p className="text-xs text-muted-foreground">enrolled {new Date(s.enrolledAt).toLocaleString()}</p>
                </div>
                <div className="text-xs text-muted-foreground shrink-0">
                  {s.status} · day {s.currentDay}/10
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editorDay && (
        <CampaignEditorModal
          campaignId={campaignId}
          templates={templates}
          screenshotMap={screenshots}
          startDay={editorDay}
          onClose={() => setEditorDay(null)}
          onSave={handleSaveTemplate}
        />
      )}

      {sendOpen && (
        <SendDialog
          campaign={campaign}
          templates={templates}
          onClose={() => setSendOpen(false)}
          onSent={async (msg) => { setInfo(msg); setSendOpen(false); await reload(); }}
        />
      )}
    </div>
  );
}

// --- Editor modal ---
//
// Split-pane: editor on the left, live iframe preview on the right.
// The iframe hits GET /api/campaigns/:id/preview/:day which renders the same
// HTML the cron sender would produce — including any customHtml override.
// We bump `previewBust` after each save so the iframe re-fetches with the
// freshly persisted fields. Mid-edit the iframe stays stale on purpose so
// the admin can compare draft text vs last-saved render side by side.
function CampaignEditorModal({ campaignId, templates, screenshotMap, startDay, onClose, onSave }: {
  campaignId: number;
  templates: Template[];
  screenshotMap: Map<string, Screenshot>;
  startDay: number;
  onClose: () => void;
  onSave: (day: number, fields: Partial<Template>) => Promise<Template>;
}) {
  const sorted = useMemo(() => [...templates].sort((a, b) => a.day - b.day), [templates]);
  const [day, setDay] = useState(startDay);
  const current = sorted.find((t) => t.day === day) || sorted[0];

  const [draft, setDraft] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [showHtmlOverride, setShowHtmlOverride] = useState(false);
  // Initialize with `Date.now()` (not 0) so the very first iframe load uses a
  // unique cache key per modal-mount. Otherwise reopening the modal hits the
  // exact same `?v=0` URL and the browser may serve a stale "Screenshot
  // pending" placeholder cached from before the screenshot was uploaded.
  const [previewBust, setPreviewBust] = useState(() => Date.now());

  useEffect(() => {
    if (current) {
      setDraft({
        subject: current.subject,
        introText: current.introText,
        valuePropText: current.valuePropText,
        featureName: current.featureName,
        featureBlurb: current.featureBlurb,
        screenshotKey: current.screenshotKey,
        ctaLabel: current.ctaLabel,
        ctaPath: current.ctaPath,
        tomorrowTeaser: current.tomorrowTeaser,
        customHtml: current.customHtml || "",
      });
      setShowHtmlOverride(!!current.customHtml);
      setErr(null); setSavedAt(null);
      // Bump cache buster on every day-switch so the iframe re-fetches fresh
      // HTML even if the same URL was previously cached for this day.
      setPreviewBust(Date.now());
    }
  }, [day, current?.id]);

  // "Dirty" = any tracked field differs from the last-saved template, so we
  // can warn the admin that the live preview is showing the prior version.
  const isDirty = useMemo(() => {
    if (!current) return false;
    const fields = ["subject", "introText", "valuePropText", "featureName", "featureBlurb", "screenshotKey", "ctaLabel", "ctaPath", "tomorrowTeaser"] as const;
    if (fields.some((k) => (draft as any)[k] !== (current as any)[k])) return true;
    const draftHtml = showHtmlOverride ? (draft.customHtml || "") : "";
    const currHtml = current.customHtml || "";
    return draftHtml !== currHtml;
  }, [draft, current, showHtmlOverride]);

  // Hooks must run unconditionally — declare before any early return.
  // Sorted list of all uploaded screenshot keys; powers the dropdown picker.
  const validKeys = useMemo(() => Array.from(screenshotMap.keys()).sort(), [screenshotMap]);
  // "Did you mean?" — narrow prefix/token match so we don't suggest random
  // unrelated keys for a 2-letter typo. Rules:
  //   - input must be ≥4 chars,
  //   - prefer real prefix overlap in either direction (catches "investor-portfolio-dashboard" → "investor-portfolio-dashboard-view"),
  //   - else accept hyphen-token overlap (≥2 shared tokens), reject single-token coincidences.
  const suggestedKey = useMemo(() => {
    const raw = (draft.screenshotKey || "").trim().toLowerCase();
    if (raw.length < 4 || validKeys.includes(draft.screenshotKey || "")) return null;
    const byPrefix = validKeys.find((v) => {
      const lv = v.toLowerCase();
      return lv.startsWith(raw) || raw.startsWith(lv);
    });
    if (byPrefix) return byPrefix;
    const inputTokens = new Set(raw.split("-").filter((t: string) => t.length >= 3));
    if (inputTokens.size < 2) return null;
    const byTokens = validKeys.find((v) => {
      const vTokens = v.toLowerCase().split("-").filter((t) => t.length >= 3);
      const shared = vTokens.filter((t) => inputTokens.has(t)).length;
      return shared >= 2;
    });
    return byTokens || null;
  }, [draft.screenshotKey, validKeys]);

  if (!current) return null;
  const screenshot = screenshotMap.get(draft.screenshotKey || current.screenshotKey);

  const handleSave = async () => {
    setSaving(true); setErr(null); setSavedAt(null);
    try {
      const fields: Partial<Template> = {
        subject: draft.subject, introText: draft.introText, valuePropText: draft.valuePropText,
        featureName: draft.featureName, featureBlurb: draft.featureBlurb,
        screenshotKey: draft.screenshotKey, ctaLabel: draft.ctaLabel, ctaPath: draft.ctaPath,
        tomorrowTeaser: draft.tomorrowTeaser,
      };
      // customHtml: send only when override toggle is ON; null clears
      (fields as any).customHtml = showHtmlOverride && draft.customHtml.trim().length > 0 ? draft.customHtml : null;
      await onSave(day, fields);
      setSavedAt(Date.now());
      // Bump cache buster so the preview iframe re-fetches the just-saved render.
      setPreviewBust((n) => n + 1);
    } catch (e: any) {
      setErr(e?.message || "Save failed");
    }
    setSaving(false);
  };

  // Day-switch guard: warn before discarding unsaved edits.
  const goToDay = (newDay: number) => {
    if (isDirty && !confirm("You have unsaved changes on this day. Discard them?")) return;
    setDay(Math.max(1, Math.min(10, newDay)));
  };

  // Close-guard: any close path (backdrop, X, Close button) must warn before
  // silently throwing away unsaved edits. Same UX as the day-switch guard.
  const requestClose = () => {
    if (isDirty && !confirm("You have unsaved changes. Discard them and close?")) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={requestClose}>
      <div className="bg-background border border-border rounded-lg w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/20">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-md bg-[#FF5C00]/10 text-[#FF5C00] flex items-center justify-center font-mono text-sm font-semibold shrink-0">D{current.day}</div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold truncate">Edit Day {current.day} of 10</h3>
              <p className="text-xs text-muted-foreground truncate">{current.subject}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => goToDay(day - 1)} disabled={day === 1} className="p-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-30" title="Previous day"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs text-muted-foreground tabular-nums">D{day}/10</span>
            <button onClick={() => goToDay(day + 1)} disabled={day === 10} className="p-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-30" title="Next day"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={requestClose} className="ml-1 p-1.5 rounded-md border border-border hover:bg-muted" title="Close"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Body: editor on left, live preview on right.
            Split-pane kicks in at md (≥768px). Below that we stack vertically
            and pin a min-height on the iframe row so the preview is never
            invisible at the bottom. */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 overflow-hidden grid-rows-[auto_minmax(280px,1fr)] md:grid-rows-1">
          {/* Editor */}
          <div className="overflow-y-auto p-5 space-y-3 text-sm md:border-r border-b md:border-b-0 border-border">
            {err && <div className="p-2 rounded-md text-xs border bg-red-500/10 border-red-500/30 text-red-700">{err}</div>}
            <Field label="Subject" value={draft.subject || ""} onChange={(v) => setDraft({ ...draft, subject: v })} />
            <Field label="Intro" value={draft.introText || ""} onChange={(v) => setDraft({ ...draft, introText: v })} multiline />
            <Field label="Value prop" value={draft.valuePropText || ""} onChange={(v) => setDraft({ ...draft, valuePropText: v })} multiline />
            <Field label="Feature name" value={draft.featureName || ""} onChange={(v) => setDraft({ ...draft, featureName: v })} />
            <Field label="Feature blurb" value={draft.featureBlurb || ""} onChange={(v) => setDraft({ ...draft, featureBlurb: v })} multiline />
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA label" value={draft.ctaLabel || ""} onChange={(v) => setDraft({ ...draft, ctaLabel: v })} />
              <Field label="CTA path" value={draft.ctaPath || ""} onChange={(v) => setDraft({ ...draft, ctaPath: v })} />
            </div>
            <Field label="Tomorrow teaser" value={draft.tomorrowTeaser || ""} onChange={(v) => setDraft({ ...draft, tomorrowTeaser: v })} />
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Screenshot key
                <span className="ml-2 normal-case font-normal text-[10px] text-muted-foreground">(pick one — only uploaded keys render an image)</span>
              </label>
              {/* Native dropdown so admins can pick from the list of uploaded keys
                  instead of guessing. The free-text input still accepts custom
                  values for forward-compatibility, and we surface a "Did you mean?"
                  suggestion when the entered value isn't a real uploaded key. */}
              <select
                value={validKeys.includes(draft.screenshotKey || "") ? (draft.screenshotKey || "") : ""}
                onChange={(e) => setDraft({ ...draft, screenshotKey: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border bg-background"
              >
                <option value="">— pick an uploaded screenshot —</option>
                {validKeys.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
              <input
                value={draft.screenshotKey || ""}
                onChange={(e) => setDraft({ ...draft, screenshotKey: e.target.value })}
                placeholder="…or type a custom key"
                className="mt-2 w-full px-3 py-2 text-xs font-mono rounded-md border border-border bg-background"
              />
              {screenshot ? (
                <img src={screenshot.publicUrl} alt={draft.screenshotKey} className="mt-2 max-h-40 rounded border border-border" />
              ) : (
                <div className="mt-1 space-y-1">
                  <p className="text-xs text-amber-700 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> No screenshot uploaded for "{draft.screenshotKey || "(empty)"}". The email will render without an image.
                  </p>
                  {suggestedKey && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, screenshotKey: suggestedKey })}
                      className="text-xs text-[#FF5C00] hover:underline inline-flex items-center gap-1"
                    >
                      Did you mean <span className="font-mono">{suggestedKey}</span>? Click to use it.
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="border-t border-border pt-3">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={showHtmlOverride} onChange={(e) => setShowHtmlOverride(e.target.checked)} />
                <span className="font-semibold uppercase tracking-wide text-muted-foreground">Use my own HTML for this day (overrides AI render)</span>
              </label>
              {showHtmlOverride && (
                <textarea value={draft.customHtml} onChange={(e) => setDraft({ ...draft, customHtml: e.target.value })}
                  rows={8}
                  placeholder="Paste full email HTML. Token {{unsubscribe_url}} is replaced at send time; if absent, an unsubscribe footer is auto-appended."
                  className="mt-2 w-full px-3 py-2 text-xs font-mono rounded-md border border-border bg-background" />
              )}
            </div>
          </div>

          {/* Live preview */}
          <div className="overflow-hidden flex flex-col bg-muted/20">
            <div className="px-4 py-2 border-b border-border bg-background flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Live preview
                {isDirty && <span className="text-amber-700 ml-1">(unsaved edits not shown — save to refresh)</span>}
              </p>
              {savedAt && Date.now() - savedAt < 5000 && (
                <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Saved</span>
              )}
            </div>
            <iframe
              key={previewBust}
              title={`Preview campaign ${campaignId} D${current.day}`}
              src={`/api/campaigns/${campaignId}/preview/${current.day}?token=${encodeURIComponent(ADMIN())}&v=${previewBust}`}
              className="flex-1 w-full bg-white"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between">
          <button onClick={requestClose} className="px-3 py-2 text-xs rounded-md border border-border hover:bg-muted">Close</button>
          <button onClick={handleSave} disabled={saving || !isDirty} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" />
            {saving ? `Saving Day ${day}…` : `Save Day ${day}`}
          </button>
        </div>
      </div>

      {/* Talking loader for the save round-trip — covers the (rare) slow case
          where DB / preview re-fetch combined exceeds the no-spinner threshold.
          Static spinners are forbidden in this project. */}
      <ProgressLoader
        active={saving}
        title={`Saving Day ${day}`}
        stages={["Validating fields", "Updating template row", "Refreshing preview"]}
        stageIntervalMs={900}
      />
    </div>
  );
}

function Field({ label, value, onChange, multiline }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)}
          rows={2}
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border bg-background" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-border bg-background" />
      )}
    </div>
  );
}

// --- Send dialog ---
interface PickerInvestor {
  id: number;
  name: string;
  email: string;
  status: string;
  type: string | null;
  firm: string | null;
}

function SendDialog({ campaign, templates, onClose, onSent }: {
  campaign: Campaign; templates: Template[];
  onClose: () => void; onSent: (msg: string) => Promise<void> | void;
}) {
  const [investors, setInvestors] = useState<PickerInvestor[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState("");
  const [extrasText, setExtrasText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrides, setOverrides] = useState<Record<number, string>>({});

  // Pull the investor list once when the dialog opens. Default selection
  // is every 'active' investor — that's the common case (send the next
  // onboarding batch to everyone you've onboarded so far). Inactive rows
  // are still shown but unchecked, with a clear pill.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api<PickerInvestor[]>(`/admin/investors`);
        if (cancelled) return;
        setInvestors(list);
        setSelectedIds(new Set(list.filter((i) => i.status === "active").map((i) => i.id)));
      } catch (e: any) {
        if (!cancelled) setLoadErr(e?.message || "Failed to load investor list");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredInvestors = useMemo(() => {
    if (!investors) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return investors;
    return investors.filter((i) =>
      i.name.toLowerCase().includes(q) ||
      i.email.toLowerCase().includes(q) ||
      (i.firm || "").toLowerCase().includes(q),
    );
  }, [investors, filter]);

  const extrasParsed = useMemo(() => {
    return extrasText.split(/[\r\n,;]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
  }, [extrasText]);

  // Combined recipient list: all selected investors + extras emails,
  // de-duplicated by lowercase email. Investor names are preserved so
  // the server can write them into the email greeting.
  const recipientsPayload = useMemo(() => {
    const seen = new Set<string>();
    const lines: string[] = [];
    if (investors) {
      for (const inv of investors) {
        if (!selectedIds.has(inv.id)) continue;
        const key = inv.email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        lines.push(`${inv.email} ${inv.name}`);
      }
    }
    for (const e of extrasParsed) {
      if (seen.has(e)) continue;
      seen.add(e);
      lines.push(e);
    }
    return { text: lines.join("\n"), count: lines.length };
  }, [investors, selectedIds, extrasParsed]);

  const totalCount = recipientsPayload.count;

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const inv of filteredInvestors) next.add(inv.id);
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());

  const handleSend = async () => {
    if (totalCount === 0) { setErr("Pick at least one investor or add an extra email"); return; }
    if (totalCount > 1000) { setErr("Max 1000 recipients per send"); return; }
    if (!confirm(`Send D1 to ${totalCount} recipient${totalCount === 1 ? "" : "s"} now? They will be enrolled and receive D2-D10 on schedule.`)) return;
    setBusy(true); setErr(null);
    try {
      const overrideHtml: Record<number, string> = {};
      for (const [k, v] of Object.entries(overrides)) {
        if (typeof v === "string" && v.trim().length > 0) overrideHtml[Number(k)] = v;
      }
      const r = await api<{ sentD1: number; failed: number; totalRecipients: number }>(`/campaigns/${campaign.id}/send`, {
        method: "POST",
        body: JSON.stringify({ recipients: recipientsPayload.text, overrideHtml: showOverrides ? overrideHtml : undefined }),
      });
      await onSent(`Sent D1 to ${r.sentD1}/${r.totalRecipients}. ${r.failed > 0 ? `${r.failed} failed.` : ""} D2-D10 will fire on schedule.`);
    } catch (e: any) {
      setErr(e?.message || "Send failed");
    }
    setBusy(false);
  };

  const visibleSelectedCount = filteredInvestors.filter((i) => selectedIds.has(i.id)).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Send "{campaign.name}" to a batch</h3>
          <button onClick={onClose} className="p-1.5 rounded-md border border-border hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-3 text-sm">
          {err && <div className="p-2 rounded-md text-xs border bg-red-500/10 border-red-500/30 text-red-700">{err}</div>}
          <ProgressLoader
            active={busy}
            title={`Blasting Day 1 to ${totalCount} recipient${totalCount === 1 ? "" : "s"}`}
            stages={SEND_STAGES}
            stageIntervalMs={Math.max(800, Math.min(2500, Math.floor(20000 / SEND_STAGES.length)))}
            note="The cron will handle Day 2 through Day 10 on the schedule defined in the brief."
          />

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Pick from your investor list
                {investors && <span className="ml-2 text-muted-foreground/70 normal-case font-normal">{selectedIds.size} of {investors.length} selected</span>}
              </label>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={selectAllVisible}
                  className="px-2 py-1 text-[11px] rounded-md border border-border hover:bg-muted">
                  Select all{filter ? " visible" : ""}
                </button>
                <button type="button" onClick={clearAll}
                  className="px-2 py-1 text-[11px] rounded-md border border-border hover:bg-muted">
                  Clear
                </button>
              </div>
            </div>
            <input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name, email, or firm"
              className="w-full px-3 py-1.5 text-xs rounded-md border border-border bg-background mb-2" />
            <div className="border border-border rounded-md max-h-64 overflow-y-auto divide-y divide-border">
              {loadErr && <div className="p-3 text-xs text-red-700">{loadErr}</div>}
              {!loadErr && investors === null && <div className="p-3 text-xs text-muted-foreground">Loading investors...</div>}
              {investors !== null && filteredInvestors.length === 0 && (
                <div className="p-3 text-xs text-muted-foreground">
                  {investors.length === 0 ? "No investors in your list yet. Add some via Admin → Investors, or use Extras below." : "No investors match this filter."}
                </div>
              )}
              {filteredInvestors.map((inv) => {
                const checked = selectedIds.has(inv.id);
                const inactive = inv.status !== "active";
                return (
                  <label key={inv.id}
                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/50 ${inactive ? "opacity-70" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleOne(inv.id)} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium truncate">{inv.name}</span>
                        {inv.firm && <span className="text-[10px] text-muted-foreground truncate">· {inv.firm}</span>}
                        {inactive && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{inv.status}</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{inv.email}{inv.type ? ` · ${inv.type}` : ""}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {filter && investors && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Showing {filteredInvestors.length} of {investors.length}. {visibleSelectedCount} of these visible rows are selected.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Extras (optional)</label>
            <textarea value={extrasText} onChange={(e) => setExtrasText(e.target.value)}
              rows={2} placeholder="Add one-off emails not in your investor list. One per line, or comma-separated."
              className="mt-1 w-full px-3 py-2 text-xs font-mono rounded-md border border-border bg-background" />
            {extrasParsed.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-1">{extrasParsed.length} extra email{extrasParsed.length === 1 ? "" : "s"} parsed</p>
            )}
          </div>

          <div className="border-t border-border pt-3">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={showOverrides} onChange={(e) => setShowOverrides(e.target.checked)} />
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">Override email HTML (per day, optional)</span>
            </label>
            {showOverrides && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-muted-foreground">Paste your own HTML for any day. Empty = use AI-generated. Saved permanently to this campaign.</p>
                {templates.map((t) => (
                  <details key={t.day} className="border border-border rounded">
                    <summary className="px-3 py-2 text-xs cursor-pointer">D{t.day} — {t.subject} {t.customHtml ? <span className="ml-2 text-purple-700">(already has override)</span> : ""}</summary>
                    <textarea value={overrides[t.day] || ""} onChange={(e) => setOverrides({ ...overrides, [t.day]: e.target.value })}
                      rows={6} placeholder={t.customHtml ? "Existing override is in place. Paste new HTML to replace it." : "Paste full email HTML here..."}
                      className="w-full px-3 py-2 text-xs font-mono border-t border-border bg-background" />
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <button onClick={onClose} className="px-3 py-2 text-xs rounded-md border border-border hover:bg-muted">Cancel</button>
          <button onClick={handleSend} disabled={busy || totalCount === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-md bg-[#FF5C00] text-white hover:bg-[#FF5C00]/90 disabled:opacity-50">
            <Send className="w-3.5 h-3.5" />
            {busy ? `Sending D1 to ${totalCount}…` : `Send D1 to ${totalCount} now`}
          </button>
        </div>
      </div>
    </div>
  );
}
